import React from 'react';
import { Sparkles, Award, Users, BookOpen, Video, Calendar, Star, ChevronRight, Shield, Heart, Target, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const HomePageEnhanced = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Image Background */}
      <section className="relative bg-black text-white py-32 overflow-hidden">
        {/* Background Image with Strong Overlay for Better Text Readability */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_ansu-learning/artifacts/uazgm8f9_IMG_6741.JPG')`,
          }}
        >
          <div className="absolute inset-0 bg-black/80"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              <span className="block bg-gradient-to-r from-purple-400 to-orange-400 bg-clip-text text-transparent">
                Obed Ansu Academy
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-4 leading-relaxed max-w-3xl mx-auto font-semibold">
              Raising the Next Generation of Prophetic Voices
            </p>
            
            <p className="text-lg md:text-xl text-gray-300 mb-10 leading-relaxed max-w-3xl mx-auto">
              If you've been stuck, searching, or settling for shallow—this is your turning point.<br/>
              We're excited to share some of the most asked-about secrets when it comes to the prophetic.<br/>
              <span className="font-semibold text-white">A 12-month academy where lost believers find direction, wandering hearts find purpose, and unrooted disciples learn to stand firm in Christ.</span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
              <button
                onClick={() => navigate('/pricing')}
                className="group px-10 py-4 bg-gradient-to-r from-purple-600 to-orange-600 hover:from-purple-700 hover:to-orange-700 rounded-xl text-white font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-3xl flex items-center space-x-2"
              >
                <span>Start Your 12-Month Journey</span>
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              
              <button
                onClick={() => navigate('/assessment')}
                className="px-10 py-4 bg-white/10 hover:bg-white/20 border-2 border-white/30 rounded-xl text-white font-bold text-lg transition-all duration-300 backdrop-blur-sm"
              >
                Take Free Assessment
              </button>
            </div>
            
            <p className="text-white/80 text-base mt-6 font-medium">
              Not sure which track? Take our free 8-question assessment
            </p>
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500 to-transparent"></div>
      </section>

      {/* Welcome Video Section */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* January Course Announcement */}
          <div className="mb-8 text-center">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-orange-500/20 to-purple-500/20 border border-orange-500/30 rounded-full px-6 py-3 mb-4">
              <Sparkles className="w-5 h-5 text-orange-400" />
              <span className="text-orange-300 font-semibold">New Cohort Starting January 2026!</span>
              <Sparkles className="w-5 h-5 text-orange-400" />
            </div>
          </div>
          
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3">
              A Message from Pastor Obed Ansu
            </h2>
            <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto">
              Discover what awaits you on this transformative journey
            </p>
          </div>
          
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-purple-500/20">
            <div className="aspect-video">
              <iframe
                src="https://drive.google.com/file/d/1ziMutWiS3ogmJ6hI7nEc-DHVRlA5HPnG/preview"
                title="Welcome Message from Pastor Obed Ansu"
                className="w-full h-full"
                allow="autoplay; encrypted-media"
                allowFullScreen
              ></iframe>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-purple-300 mb-4">Don't miss the January 2026 intake!</p>
            <button
              onClick={() => navigate('/signup')}
              className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-purple-600 hover:from-orange-600 hover:to-purple-700 rounded-xl text-white font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              <span>Reserve Your Spot</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">This Academy Is For You If...</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Stop reading Christian books hoping something will click. You need structure. You need a father. You need a year that changes everything.</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="flex items-start space-x-4 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">You're Spiritually Stuck</h3>
                <p className="text-gray-600 text-sm">You've been saved for years but still feel lost. You know there's more, but you don't know how to access it.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="w-2 h-2 bg-orange-600 rounded-full mt-2 flex-shrink-0"></div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">You Need Accountability</h3>
                <p className="text-gray-600 text-sm">You're tired of starting strong and fizzling out. You need someone to hold you to the standard God called you to.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">You Want to Build Godly Habits</h3>
                <p className="text-gray-600 text-sm">No more spiritual sprints. This is a year to establish daily rhythms that anchor you in prayer, Scripture, and the presence of God.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="w-2 h-2 bg-orange-600 rounded-full mt-2 flex-shrink-0"></div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">You're Hungry for Direction</h3>
                <p className="text-gray-600 text-sm">You sense a calling but lack clarity. You need a spiritual father to help you discern God's voice and walk in your assignment.</p>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-10">
            <p className="text-xl font-bold text-gray-900 mb-2">If you saw yourself in any of these—you're in the right place.</p>
            <p className="text-lg text-gray-600">Join <span className="font-semibold text-purple-600">100+ students from around the world</span> who refused to stay stuck.</p>
          </div>
        </div>
      </section>

      {/* Stats Section - Neutral */}
      <section className="py-16 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">12</div>
              <div className="text-gray-600">Months Program</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">100+</div>
              <div className="text-gray-600">Video Lessons</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">24/7</div>
              <div className="text-gray-600">AI Support</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">Live</div>
              <div className="text-gray-600">Q&A Sessions</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Programs - Minimal Accent Colors */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-gray-100 rounded-full px-4 py-2 mb-4">
              <Award className="w-4 h-4 text-gray-700" />
              <span className="text-gray-700 text-sm font-semibold">12-Month Transformation</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What 12 Months Will Give You
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              This is not passive learning. This is active discipleship. Here's what you'll walk away with:
            </p>
            
            <div className="max-w-3xl mx-auto text-left space-y-4 mb-8">
              <div className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-gray-200">
                <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-gray-900 font-semibold mb-1">Biblical Foundation You Can Stand On</p>
                  <p className="text-gray-600 text-sm">100+ video teachings that take you from basic faith to deep theological understanding—taught with clarity and anointing.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-gray-200">
                <div className="w-2 h-2 bg-orange-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-gray-900 font-semibold mb-1">Monthly Live Access to Pastor Obed Ansu</p>
                  <p className="text-gray-600 text-sm">Not recorded replays. Real mentorship. Real impartation. Real answers to your questions.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-gray-200">
                <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-gray-900 font-semibold mb-1">A Community That Challenges You</p>
                  <p className="text-gray-600 text-sm">No lukewarm believers here. You'll be surrounded by hungry, serious students who sharpen you.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-gray-200">
                <div className="w-2 h-2 bg-orange-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-gray-900 font-semibold mb-1">24/7 AI Mentor for Accountability</p>
                  <p className="text-gray-600 text-sm">Stuck at 2 AM with a spiritual question? Get instant biblical guidance anytime you need it.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-gray-200">
                <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-gray-900 font-semibold mb-1">Assignments That Build Discipline</p>
                  <p className="text-gray-600 text-sm">This isn't watch-and-forget. You'll receive practical assignments to establish lifelong spiritual habits.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-gray-200">
                <div className="w-2 h-2 bg-orange-600 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-gray-900 font-semibold mb-1">Certificate of Completion</p>
                  <p className="text-gray-600 text-sm">Not just a certificate—a testament that you finished what you started and are ready to lead.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Apostolic-Prophetic Track */}
            <div className="group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-purple-300">
              <div className="h-2 bg-gradient-to-r from-purple-600 to-purple-700"></div>
              
              <div className="p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                    <Target className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-purple-600 uppercase tracking-wider">Track 1</div>
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Apostolic & Prophetic Leadership
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  For those called to preach, prophesy, teach, or lead in ministry—but lack the foundation to do it with authority.
                </p>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-purple-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <Shield className="w-3 h-3 text-purple-600" />
                    </div>
                    <span className="text-gray-700 text-sm">Hear God's voice clearly and discern His will with confidence</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-purple-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <Star className="w-3 h-3 text-purple-600" />
                    </div>
                    <span className="text-gray-700 text-sm">Operate in the prophetic with accuracy, not hype</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-purple-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <Users className="w-3 h-3 text-purple-600" />
                    </div>
                    <span className="text-gray-700 text-sm">Lead with humility, wisdom, and apostolic authority</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-purple-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <Heart className="w-3 h-3 text-purple-600" />
                    </div>
                    <span className="text-gray-700 text-sm">Minister with spiritual weight that people recognize</span>
                  </div>
                </div>
                
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <div className="text-sm text-gray-500">Duration</div>
                    <div className="font-semibold text-gray-900">12 Months</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">
                      From £120<span className="text-base font-normal text-gray-500">/month</span>
                    </div>
                    <div className="text-xs text-green-600 font-medium">or £1,200 (Save £200)</div>
                  </div>
                </div>
                
                <button
                  onClick={() => navigate('/pricing?track=apostolic-prophetic')}
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 group shadow-lg shadow-purple-500/30 mb-3"
                >
                  <span>Enroll Now</span>
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                
                <button
                  onClick={() => navigate('/mentorship-programs/apostolic-prophetic')}
                  className="w-full py-3 bg-white border-2 border-purple-600 text-purple-600 font-medium rounded-xl hover:bg-purple-50 transition-all duration-200 flex items-center justify-center space-x-2 group"
                >
                  <span>View Full Curriculum</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            {/* Minstrel & Worship Track */}
            <div className="group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-orange-300">
              <div className="h-2 bg-gradient-to-r from-orange-500 to-orange-600"></div>
              
              <div className="p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
                    <Star className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-orange-600 uppercase tracking-wider">Track 2</div>
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Minstrel & Worship Leadership
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  For worship leaders and musicians who want to move beyond performance into priestly ministry that carries God's presence.
                </p>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-orange-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-3 h-3 text-orange-500" />
                    </div>
                    <span className="text-gray-700 text-sm">Understand the theology of worship—not just the music</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-orange-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <Video className="w-3 h-3 text-orange-500" />
                    </div>
                    <span className="text-gray-700 text-sm">Become a glory carrier who creates and sustains atmospheres</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-orange-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <Users className="w-3 h-3 text-orange-500" />
                    </div>
                    <span className="text-gray-700 text-sm">Lead worship teams with spiritual maturity and vision</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-orange-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <Heart className="w-3 h-3 text-orange-500" />
                    </div>
                    <span className="text-gray-700 text-sm">Includes prophetic bass training to master sound as ministry</span>
                  </div>
                </div>
                
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <div className="text-sm text-gray-500">Duration</div>
                    <div className="font-semibold text-gray-900">12 Months</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">
                      From £120<span className="text-base font-normal text-gray-500">/month</span>
                    </div>
                    <div className="text-xs text-green-600 font-medium">or £1,200 (Save £200)</div>
                  </div>
                </div>
                
                <button
                  onClick={() => navigate('/pricing?track=minstrel-worship')}
                  className="w-full py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 group shadow-lg shadow-orange-500/30 mb-3"
                >
                  <span>Enroll Now</span>
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                
                <button
                  onClick={() => navigate('/mentorship-programs/minstrel-worship')}
                  className="w-full py-3 bg-white border-2 border-orange-500 text-orange-500 font-medium rounded-xl hover:bg-orange-50 transition-all duration-200 flex items-center justify-center space-x-2 group"
                >
                  <span>View Full Curriculum</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-white rounded-full px-4 py-2 mb-4 shadow-sm">
              <Star className="w-4 h-4 text-orange-500" />
              <span className="text-gray-700 text-sm font-semibold">Student Testimonials</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Don't Take Our Word For It</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Here's what students are saying after committing to 12 months of discipleship:
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Testimonial 1 - Samuel Anderson */}
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="flex text-orange-500">
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                </div>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "I would absolutely recommend this mentorship. It is a privilege to be mentored under pastor Obed. He is a great teacher AND friend. It helps you build resilience in the Word of God. If you desire to know God deeply but don't know where to start from, I strongly recommend joining this mentorship."
              </p>
              <div className="border-t border-gray-200 pt-4">
                <p className="font-semibold text-gray-900">Samuel Anderson</p>
                <p className="text-sm text-gray-500">Academy Student</p>
              </div>
            </div>

            {/* Testimonial 2 - Jeremiah */}
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="flex text-orange-500">
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                </div>
                <span className="ml-2 text-sm font-semibold text-gray-700">10/10</span>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "The most valuable insights I had was in how to work with God better, in a new dimension and deepness. It has forced me to take my walk with Christ more seriously, and has set me up well to continue on that path and go even deeper with Him."
              </p>
              <div className="border-t border-gray-200 pt-4">
                <p className="font-semibold text-gray-900">Jeremiah Dom-Ogbonna</p>
                <p className="text-sm text-gray-500">Academy Student</p>
              </div>
            </div>

            {/* Testimonial 3 - Chantelle */}
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="flex text-orange-500">
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4" />
                </div>
                <span className="ml-2 text-sm font-semibold text-gray-700">8/10</span>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "The word of God is ALIVE! My mentor made me understand how important the Word is and that alone has changed my life because I was taking the Word for granted. This has changed my perspective. I am on a journey of building discipline."
              </p>
              <div className="border-t border-gray-200 pt-4">
                <p className="font-semibold text-gray-900">Chantelle Asare</p>
                <p className="text-sm text-gray-500">Academy Student</p>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-10">
            <p className="text-lg text-gray-600">
              <span className="font-semibold text-purple-600">100+ students globally</span> have experienced transformation. Will you be next?
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid - Neutral Colors */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Everything You Need to Grow</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We've built a complete ecosystem for your spiritual formation. No excuses. No distractions. Just everything you need to go deeper with God.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-4">
                <Video className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Video Lessons</h3>
              <p className="text-gray-600">High-quality teaching videos covering foundational to advanced topics</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Live Q&A</h3>
              <p className="text-gray-600">Monthly live sessions with Pastor Obed Ansu</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Community</h3>
              <p className="text-gray-600">Connect with fellow believers on the same journey</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center mb-4">
                <BookOpen className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Ebook Library</h3>
              <p className="text-gray-600">Access exclusive spiritual resources and teachings</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Certificates</h3>
              <p className="text-gray-600">Earn certificates upon course completion</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">AI Helpdesk</h3>
              <p className="text-gray-600">24/7 AI-powered support for your learning journey</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Bold & Convictional */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/20 to-orange-900/20"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            The Year That Changes Everything<br/>Starts Today
          </h2>
          <p className="text-xl text-gray-300 mb-6">
            You've read this far because something in you knows this is what you've been looking for.
          </p>
          <p className="text-lg text-gray-400 mb-10 max-w-2xl mx-auto">
            The question is not whether you need this. The question is whether you'll act on it.<br/>
            Stop waiting for the "perfect time." The perfect time is now.<br/>
            <span className="text-white font-semibold">Your transformation doesn't start when you feel ready. It starts when you commit.</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            <button
              onClick={() => navigate('/pricing')}
              className="px-10 py-4 bg-gradient-to-r from-purple-600 to-orange-600 hover:from-purple-700 hover:to-orange-700 text-white rounded-xl font-bold text-lg transition-all duration-300 shadow-2xl transform hover:scale-105 flex items-center space-x-2"
            >
              <span>Enroll Now - Choose Your Plan</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-gray-400">
            Pay in Full (Save £200) • Quarterly • Monthly Options Available
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePageEnhanced;